import { AdvancedType, BasicType, DynamicType, IBlockData } from '@bay-email/core';

export const ATTACHED_LISTING_PICKER_EVENT = 'bay-email:open-attached-listing-picker';

const ATTACHED_LISTING_BLOCK_TYPES = new Set<string>([
	BasicType.TEXT,
	BasicType.BUTTON,
	BasicType.IMAGE,
	BasicType.TABLE,
	BasicType.SECTION,
	BasicType.COLUMN,
	AdvancedType.TEXT,
	AdvancedType.BUTTON,
	AdvancedType.IMAGE,
	AdvancedType.TABLE,
	AdvancedType.SECTION,
	AdvancedType.COLUMN,
	DynamicType.IMAGE,
	'text_single_listing',
	'listings_block',
	'criteria_block',
	'custom_listing_block',
]);

export function supportsAttachedListing(type?: string | null) {
	if (!type) return false;
	return ATTACHED_LISTING_BLOCK_TYPES.has(type);
}

/**
 * Descendants of `idx` carrying a listing binding of their own.
 *
 * A block's own binding beats its ancestor's (see `resolveBoundListing`), so
 * whoever owns a listing scope has to drop its descendants' bindings when the
 * scope's listing changes — otherwise a previously-pinned child keeps painting
 * the listing that was just replaced, and a cleared scope never goes blank.
 */
export function collectBoundDescendantIdxs(
	block: any,
	idx: string,
	result: string[],
) {
	if (!Array.isArray(block?.children)) return;

	block.children.forEach((child: any, childIndex: number) => {
		const childIdx = `${idx}.children.[${childIndex}]`;
		const value = child?.data?.value;

		if (value?.selectedListingId || value?.selectedListing) {
			result.push(childIdx);
		}

		collectBoundDescendantIdxs(child, childIdx, result);
	});
}

// Sticky target for the open request. When the picker action retargets to an
// ancestor (the Custom Listing container), that panel isn't mounted yet at
// dispatch time — it mounts after focus moves. The panel consumes this on mount
// so the drawer still opens; the live event covers the already-mounted case.
let pendingListingPickerIdx: string | null = null;

export function dispatchOpenAttachedListingPicker(idx?: string | null) {
	if (!idx || typeof window === 'undefined') return;

	pendingListingPickerIdx = idx;
	window.dispatchEvent(
		new CustomEvent(ATTACHED_LISTING_PICKER_EVENT, {
			detail: { idx },
		}),
	);
}

// Returns true (and clears) if there's a pending open request for this idx.
export function consumePendingListingPickerOpen(idx?: string | null): boolean {
	if (idx && pendingListingPickerIdx === idx) {
		pendingListingPickerIdx = null;
		return true;
	}
	return false;
}

/**
 * Concatenates every cell's HTML from an advanced table's `tableSource` grid.
 * Advanced tables store per-cell content at `data.value.tableSource[r][c].content`
 * (not the single `data.value.content` string a basic table uses), so listing
 * merge-tag detection has to look across all cells.
 */
export function collectAdvancedTableCellContent(block?: IBlockData | null): string {
	const tableSource = (block?.data?.value as { tableSource?: unknown } | undefined)
		?.tableSource;
	if (!Array.isArray(tableSource)) return '';

	return tableSource
		.flatMap(row => (Array.isArray(row) ? row : []))
		.map(cell => {
			const content = (cell as { content?: unknown } | null | undefined)?.content;
			return typeof content === 'string' ? content : '';
		})
		.join(' ');
}

export function usesListingMergeTags(value?: string | null) {
	if (!value) return false;

	const content = String(value)
		.replace(/&quot;/gi, '"')
		.replace(/&#39;|&apos;/gi, "'");

	return (
		/\{\{[\s\S]*?\blisting\./i.test(content) ||
		/data-tag-key\s*=\s*(?:"|'|&quot;)\s*listing\./i.test(content) ||
		/data-dynamic-tag\s*=\s*(?:"|')[^"']*\blisting\./i.test(content)
	);
}

/**
 * KNOWN WRONG, deliberately left alone — read this before "fixing" it.
 *
 * This checks only `selectedListing`, the pre-migration resolved copy. The
 * current model persists `selectedListingId` and writes `selectedListing: null`
 * (see `onSelectListing` in AttachedListingSection), so this returns false for
 * every binding made since that change. `resolveBoundListing` is the predicate
 * that reads both and is the one to trust.
 *
 * Two consequences today:
 *
 *   1. `shouldShowListingMergeTagsForBlock` hides listing merge tags in Office
 *      for blocks that genuinely have a listing attached. This is the visible
 *      bug.
 *   2. `useClearAttachedListingWhenUnused` short-circuits on `!hasAttached`, so
 *      it has never cleared a modern binding — the thing it exists to do.
 *
 * Widening this to `selectedListingId || selectedListing` fixes (1) and arms
 * (2) in the same edit, and (2) DELETES bindings. Before doing that, satisfy
 * yourself that no caller can report "no listing merge tags" for a render while
 * a listing is attached — the Section panel passes a hard `false`, so every
 * section holding a legacy listing gets wiped on focus. That may well be the
 * intent (see the comment in the Section panel), but it is a data change, not a
 * display change, and it wants its own ticket.
 *
 * ponytail: legacy-field-only predicate; widen to cover selectedListingId once
 * the clear-hook blast radius above has been walked.
 */
export function hasAttachedListing(block?: IBlockData | null) {
	return Boolean(block?.data?.value?.selectedListing);
}

/**
 * Collects the string fields of a block (and all descendants) that may contain
 * merge tags — text content, link href, image src, dynamic image tag, etc.
 * Used to detect listing merge tag usage across a container's subtree.
 */
export function collectBlockMergeTagValues(block?: IBlockData | null): string[] {
	if (!block) return [];

	const value = (block.data?.value ?? {}) as Record<string, unknown>;
	const attributes = (block.attributes ?? {}) as Record<string, unknown>;
	// Multiple / By Criteria hrefs bind their own listings, never an attached one.
	const isLoopListingBlock = block.type === 'listings_block' || block.type === 'criteria_block';
	const own = [
		value.content,
		value.href,
		value.dynamicImageTag,
		isLoopListingBlock ? undefined : attributes.href,
		attributes.src,
	].filter((s): s is string => typeof s === 'string' && s.length > 0);

	const children = Array.isArray(block.children)
		? block.children.flatMap(child => collectBlockMergeTagValues(child))
		: [];

	return [...own, ...children];
}

/**
 * True if the block or any of its descendants use listing merge tags. Lets
 * container blocks (section/column) show "Listing Settings" only when a
 * descendant will actually consume the attached listing's data.
 */
export function blockTreeUsesListingMergeTags(block?: IBlockData | null): boolean {
	return collectBlockMergeTagValues(block).some(usesListingMergeTags);
}

export function shouldShowListingMergeTagsForBlock(
	block?: IBlockData | null,
	isDesignAdmin?: boolean,
) {
	if (!block) return true;
	if (isDesignAdmin) return true;
	if (!supportsAttachedListing(block.type)) return true;
	return hasAttachedListing(block);
}
